import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/format";
import { Eye } from "lucide-react";

const PAGE_SIZE = 30;

const ACTION_META = {
  create: { label: "Creazione", cls: "bg-green-50 text-green-700 border-green-200" },
  update: { label: "Modifica", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  delete: { label: "Eliminazione", cls: "bg-red-50 text-red-700 border-red-200" },
  rinnovo: { label: "Rinnovo", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const ENTITIES = ["clienti", "subvettori", "localita", "prodotti", "veicoli", "autisti", "scadenze", "users"];

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({});
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (debounced) params.search = debounced;
    if (filters.entity) params.entity = filters.entity;
    if (filters.action) params.action = filters.action;
    api
      .get("/audit", { params })
      .then((r) => {
        setRows(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [page, debounced, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    { key: "timestamp", label: "Data/ora", nowrap: true, sortable: false, render: (r) => formatDateTime(r.timestamp) },
    { key: "user", label: "Utente", sortable: false, render: (r) => <span className="font-medium">{r.user}</span> },
    {
      key: "action",
      label: "Azione",
      sortable: false,
      render: (r) => {
        const m = ACTION_META[r.action] || { label: r.action, cls: "" };
        return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
      },
    },
    { key: "entity", label: "Entità", sortable: false },
    { key: "record_id", label: "ID record", sortable: false, render: (r) => <span className="text-xs text-slate-400 font-mono">{(r.record_id || "").slice(0, 8)}</span> },
  ];

  const filterFields = [
    { name: "entity", label: "Entità", options: ENTITIES.map((e) => ({ value: e, label: e })) },
    { name: "action", label: "Azione", options: Object.keys(ACTION_META).map((a) => ({ value: a, label: ACTION_META[a].label })) },
  ];

  return (
    <div data-testid="audit-page">
      <DataTable
        testid="table-audit"
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPage={setPage}
        onSort={null}
        sortBy={null}
        search={search}
        onSearch={setSearch}
        filterFields={filterFields}
        filterValues={filters}
        onFilter={(name, val) => {
          setFilters((p) => ({ ...p, [name]: val }));
          setPage(1);
        }}
        emptyLabel="Nessuna attività registrata"
        renderActions={(row) => (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-sky-700" onClick={() => setDetail(row)} data-testid={`audit-detail-${row.id}`}>
            <Eye className="h-4 w-4" />
          </Button>
        )}
      />

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail">
          <DialogHeader>
            <DialogTitle className="font-head">Dettaglio attività</DialogTitle>
            <DialogDescription>Valore precedente e nuovo valore del record modificato.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400 text-xs">Utente</span><div>{detail.user} ({detail.user_role})</div></div>
                <div><span className="text-slate-400 text-xs">Data/ora</span><div>{formatDateTime(detail.timestamp)}</div></div>
                <div><span className="text-slate-400 text-xs">Azione</span><div>{ACTION_META[detail.action]?.label || detail.action}</div></div>
                <div><span className="text-slate-400 text-xs">Entità</span><div>{detail.entity}</div></div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Valore precedente</div>
                <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-xs overflow-auto max-h-48">{JSON.stringify(detail.old_value, null, 2) || "—"}</pre>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Nuovo valore</div>
                <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-xs overflow-auto max-h-48">{JSON.stringify(detail.new_value, null, 2) || "—"}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
